import { Carousel, type Embla } from '@mantine/carousel'
import {
  AspectRatio,
  Avatar,
  Box,
  type BoxProps,
  Card,
  Flex,
  Image,
  Skeleton,
} from '@mantine/core'
import clsx from 'clsx'
import { useCallback, useEffect, useState } from 'react'

import { type CoreProduct } from '@flecto/types'
import { getImageUrl } from '@flecto/utils'

import { useAccessibilityProps } from '../../hooks/useAccessibilityProps'
import styles from './styles.module.css'

export type CardProductPhotosGalleryProps = BoxProps & {
  product?: CoreProduct
  isLoading?: boolean
}

export function CardProductPhotosGallery({
  product,
  isLoading = true,
  ...props
}: CardProductPhotosGalleryProps) {
  const [embla, setEmbla] = useState<Embla | null>(null)
  const [currentSlide, setCurrentSlide] = useState(0)

  const acessibilityProps = useAccessibilityProps({
    type: 'product',
    content: product?.title ?? '',
  })

  const classes = clsx(styles.gallery, props.className)

  const handleAvatarClick = useCallback(
    (slide: number) => {
      embla.scrollTo(slide)
      setCurrentSlide(slide)
    },
    [embla]
  )

  useEffect(() => {
    if (product?.photos) {
      const mainPhoto = product.photos.findIndex((p) => p.main)

      if (mainPhoto) {
        setCurrentSlide(mainPhoto)
      }
    }
  }, [product])

  useEffect(() => {
    if (embla) {
      handleSelect()
      embla.on('select', handleSelect)

      return () => {
        embla.off('select', handleSelect)
      }
    }

    return undefined
  }, [embla])

  const handleSelect = useCallback(() => {
    if (!embla) return
    const slide = embla.selectedScrollSnap()
    setCurrentSlide(slide)
  }, [embla, setCurrentSlide])

  if (isLoading) {
    return (
      <Card {...props} className={classes}>
        <Flex gap='sm' direction='column'>
          <Skeleton h={40} w={40} />
          <Skeleton h={40} w={40} />
          <Skeleton h={40} w={40} />
          <Skeleton h={40} w={40} />
        </Flex>
        <Flex p='xl' align='center' justify='center' style={{ flex: 1 }}>
          <Skeleton h={300} />
        </Flex>
      </Card>
    )
  }

  if (!product) {
    return
  }

  return (
    <Card {...props} className={classes}>
      <Box className={styles.thumbnails}>
        {product?.photos?.map((p, key) => (
          <Avatar
            src={getImageUrl(p.photo_url)}
            key={p.id}
            size='lg'
            className={clsx(
              { [styles.current]: key === currentSlide },
              styles.avatar
            )}
            onClick={() => handleAvatarClick(key)}
            {...acessibilityProps}
          />
        ))}
      </Box>
      <Carousel
        w='100%'
        h='100%'
        loop={true}
        getEmblaApi={setEmbla}
        initialSlide={0}
        className={styles.carousel}
      >
        {product?.photos?.map((p) => (
          <Carousel.Slide key={p.id}>
            <AspectRatio ratio={1 / 1} maw='70%' mx='auto' w='100%'>
              <Image src={getImageUrl(p.photo_url)} {...acessibilityProps} />
            </AspectRatio>
          </Carousel.Slide>
        ))}
      </Carousel>
    </Card>
  )
}
